import type { Router } from "vue-router";

/**
 * A page scrolls inside its own element, never the window, so vue-router's `savedPosition`
 * has nothing to hand back and every return to a list landed at the top. This remembers the
 * scroller's offset on the way out and puts it back on the way in.
 *
 * Keyed by `path`, not `fullPath`: a list normalises its filters into the query as it mounts,
 * so the address you come back to is not character for character the one you left.
 */
const PAGE_REGION = ".main-content";

/**
 * How long to keep trying. The rows arrive with the query — a network round trip
 * after the route resolves — so a handful of frames is not nearly enough; that is
 * what left the list at the top even once it knew where to go.
 */
const RESTORE_BUDGET_MS = 3_000;

const keyFor = (path: string) => `scroll_${path}`;

/**
 * Which element actually scrolls differs from page to page: the Runs list owns a
 * flex child of its own, a DataTable page scrolls PrimeVue's
 * `.p-datatable-table-container`, and a short page scrolls the region itself. So the
 * page's own scroller is found rather than named — the tallest scrollable descendant
 * of the content region, of which every list page has exactly one. (Naming one
 * selector is what made the previous attempt a no-op on both of those lists.)
 */
const scrollContainer = (): HTMLElement | null => {
  const region = document.querySelector<HTMLElement>(PAGE_REGION);
  if (!region) return null;

  let tallest: HTMLElement | null = null;
  for (const element of region.querySelectorAll<HTMLElement>("*")) {
    if (element.scrollHeight <= element.clientHeight) continue;
    if (!/auto|scroll/.test(getComputedStyle(element).overflowY)) continue;
    if (!tallest || element.scrollHeight > tallest.scrollHeight) tallest = element;
  }
  return tallest ?? region;
};

const remember = (path: string): void => {
  const container = scrollContainer();
  if (!container) return;
  try {
    // sessionStorage rather than a module-level map, so a full page load — a stale
    // build reloading itself, say — does not take your place in the list with it.
    sessionStorage.setItem(keyFor(path), String(container.scrollTop));
  } catch {
    // Blocked storage costs the restore, nothing else.
  }
};

/** Consumed on read: a later visit to the same list is a fresh one, and starts at the top. */
const takeRemembered = (path: string): number => {
  try {
    const saved = Number(sessionStorage.getItem(keyFor(path)));
    sessionStorage.removeItem(keyFor(path));
    return Number.isFinite(saved) ? saved : 0;
  } catch {
    return 0;
  }
};

const scrollTo = async (target: number): Promise<void> => {
  // Waiting for rows means the user may well start reading — or scrolling — before
  // they arrive. Whatever they do with the page outranks where they last left it.
  let cancelled = false;
  const cancel = () => (cancelled = true);
  const events = ["wheel", "touchstart", "keydown", "pointerdown"] as const;
  events.forEach((event) => window.addEventListener(event, cancel, { once: true, passive: true }));

  try {
    const deadline = Date.now() + RESTORE_BUDGET_MS;
    while (!cancelled && Date.now() < deadline) {
      const container = scrollContainer();
      if (container && container.scrollHeight - container.clientHeight >= target) {
        container.scrollTop = target;
        return;
      }
      await new Promise((resolve) => requestAnimationFrame(resolve));
    }
    // The list never grew back to that height — filters changed, rows are gone. The
    // top is a better place to land than a guess at where the old offset now points.
  } finally {
    events.forEach((event) => window.removeEventListener(event, cancel));
  }
};

export function restoreScrollPositions(router: Router): void {
  router.beforeEach((_to, from) => {
    remember(from.path);
    return true;
  });

  router.afterEach((to) => {
    const target = takeRemembered(to.path);
    if (target > 0) void scrollTo(target);
  });
}
